import { MAX_REPORT_COMPETITORS } from '../components/PageConfigProvider/constants';
import {
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import { PlotFilterType } from '../components/PageConfigProvider/types/item';
import { FilterId } from '../filters/types/filters';
import { buildReportPages } from './report-page-builder';

const companyParam = { parameterId: 'company-parameter', grouped: false };
const competitorsGrouped = {
  parameterId: 'competitors-parameter',
  grouped: true,
};
const competitorsUngrouped = {
  parameterId: 'competitors-parameter',
  grouped: false,
};

export const reportCompanyGenderDiversity: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('company-gender-diversity', [
    [
      {
        id: 'about-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'ai-summary',
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: 'intro-description',
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml:
            '## What is a gender diversity report?\n\nRevelio Labs\' gender diversity report provides an overview of the gender diversity at {{PARAM "Company"}}. Using Revelio Labs individual data, which includes millions of online professional profiles, we predict an individual\'s gender using their first name. Our gender prediction model is trained on millions of records from the social security administration data to estimate the probabilities that a name is male or female.\n\nIn the first part of the report, we assess the gender diversity of the workforce at {{PARAM "Company"}} worldwide by comparing different diversity metrics to the overall industry. We then zoom into different granularities to analyze the countries and roles where women are underrepresented.\n\nWhen analyzing {{PARAM "Company"}}\'s workforce composition, we consider dimensions such as **Industry Peers**, **Geographical Location**, **Job Roles**, and **Seniority**.',
        },
      },
      {
        id: 'benchmarking1-text',
        title: '',
        position: { width: 24, y: 14, height: 16 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Share of women over time\n\nThe gender diversity of a company depends not only on its historical trends and recent initiatives, but also on its talent pool and the standards within the industry. Using our competitors model, we gather the top competitors to {{PARAM "Company"}} and use them to form a comparison set to benchmark against.\n\nAnalyzing the proportion of females within a company, relative to its peers, can provide valuable insight into its progress towards organizational equality. The adjacent figure shows how {{PARAM "Company"}} \'s female share has compared to its peers historically.',
          plotParameters: [companyParam],
        },
      },
      {
        id: 'benchmarking1-line',
        title: 'Share of women in the workforce (global)',
        position: { x: 25, y: 14, width: 23, height: 16 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['headcount'],
          secondaryDimension: 'gender',
          showLegend: true,
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'benchmarking2-text',
        title: '',
        position: { width: 24, height: 14 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Where does {{PARAM "Company"}} stand relative to its peers?\n\nTo know where {{PARAM "Company"}} stands relative to the industry, we examine the share of women across its top competitors. {{PARAM "Company"}} currently ranks {{FORMAT (RANK "female_share" (PARAM "Company")) format="ORDINAL"}} among its peer companies in terms of the overall share of women in the workforce.',
        },
      },
      {
        id: 'benchmarking2-bar',
        title: 'Ranking among peers: Share of women',
        position: { x: 25, width: 23, height: 14 },
        plot: {
          chartType: 'bar',
          dimensionSource: 'competitors',
          defaultMetricOptions: ['headcount'],
          secondaryDimension: 'gender',
          plotFilters: [{ type: PlotFilterType.TOP_N, value: 20 }],
          plotParameters: [companyParam, competitorsUngrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'benchmarking3-text',
        title: '',
        position: { y: 15, height: 5 },
        plot: {
          chartType: 'text',
          textHtml:
            "## Drivers of Growth: Inflows & Outflows\n\nGrowth in the share of women is a function of both inflows and outflows. For example, a company may increase its share of women by hiring more women, or equally by laying off several of their male counterparts. Understanding these drivers of female share growth can help disambiguate the underlying cause of a company’s organizational inequality.\n\nIn the figure below, we show the share of women in the company's count, inflows, and outflows. If the share of women in inflows is larger than the share of women in the headcount, women are overrepresented in inflows relative to their headcount.",
        },
      },
      {
        id: 'benchmarking3-stacked',
        title: 'Share of women in inflows and outflows (global)',
        position: { x: 8, y: 20, width: 32, height: 12 },
        plot: {
          chartType: 'stacked-bar',
          defaultMetricOptions: [
            'transitions_inflow',
            'transitions_outflow',
            'headcount',
          ],
          secondaryDimension: 'gender',
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'benchmarking4-text',
        title: '',
        position: { height: 6 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Women representation in different roles\n\nNext, we examine the gender composition of different roles in {{PARAM "Company"}}. Revelio Labs uses a proprietary model to classify job titles into uniform job clusters. Here we analyze the gender composition of each of the most common roles in {{PARAM "Company"}} relative to the industry.\n\n{{#if (gt (VALUE "headcount" entity=(PARAM "Company")) (AVG "headcount" excludeEntity=(PARAM "Company")))}}\nThe share of women across key roles is generally higher than the industry baseline.\n{{else}}\nThe share of women across key roles frequently trails the industry baseline.\n{{/if}}',
        },
      },
      {
        id: 'benchmarking4-diverging',
        title: 'The share of women in each role vs. Industry (p.p.)',
        position: { y: 6, height: 12 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'role',
          defaultMetricOptions: ['headcount'],
          comparisonTransformation: { baseline: 'second' },
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'benchmarking5-text',
        title: '',
        position: { y: 20, height: 4 },
        plot: {
          chartType: 'text',
          textHtml:
            "## Women representation in different levels of seniority\n\nWe leverage Revelio Labs's seniority model that standardizes millions of job titles into comparable levels of seniority, abstracting from job functions. We classify all positions at the company into three primary levels: Junior, Middle Management, and Senior Management.\n\nThe following diverging bar illustrates the share of female workers in each level of seniority compared directly to the industry peer group.",
        },
      },
      {
        id: 'benchmarking5-diverging',
        title:
          'Difference in the share of women by seniority vs. Industry (p.p.)',
        position: { y: 24, height: 8 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'seniority',
          defaultMetricOptions: ['headcount'],
          comparisonTransformation: { baseline: 'second' },
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'factors1-text',
        title: '',
        position: { width: 24, height: 14 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Gender gap in salaries\n\nOne important factor that can result in an overrepresentation of female employees in outflows is large salary gaps across genders. Our salary model predicts compensation accounting for position-specific information (job title, seniority, company, location) as well as tenure.\n\n{{#if (gt (VALUE "base_salary" entity=(PARAM "Company")) (AVG "base_salary" excludeEntity=(PARAM "Company")))}}\nCurrently, the salary distribution structure at {{PARAM "Company"}} is highly competitive compared to the industry, reflecting strong internal equity initiatives.\n{{else}}\nThe average salary metrics indicate a wider comparative gap against the peer industry, which may serve as a contributing factor for female attrition.\n{{/if}}\n\n*Note: Observed salary gaps often reflect the underlying distribution of roles and seniority occupied by different genders.*',
        },
      },
      {
        id: 'factors1-line',
        title: 'Dollars earned by women for every 1 dollar earned by men',
        position: { x: 25, width: 23, height: 14 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['base_salary'],
          secondaryDimension: 'gender',
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'factors2-imbalance-text',
        title: '',
        position: { width: 24, y: 16, height: 14 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Seniority Imbalance\n\nHaving a higher than average share of women in different roles does not always indicate gender equality. If the company only hires females in junior positions but leaves senior positions for men, then female employees are not well-represented across the organizational hierarchy.\n\nTo assess this, we evaluate the **Seniority Imbalance Score**, which tracks the representation of women above the median seniority. A negative score indicates women are clustered in junior roles, while a positive score points toward stronger senior representation.',
        },
      },
      // {
      //   id: 'factors2-imbalance-gauge',
      //   title: '',
      //   position: { x: 25, y: 198, width: 23, height: 14 },
      //   plot: {
      //     chartType: 'gauge',
      //     colorPalette: ['#E76A87', '#70CA97', '#3DC679'],
      //     defaultMetricOptions: ['seniority_imbalance_score'],
      //     gaugeSubText: 'Seniority Imbalance Score [-1, +1]',
      //     gaugeValue:
      //       '{{VALUE "seniority_imbalance_score" entity=(PARAM "Company")}}',
      //     plotParameters: [
      //       { parameterId: 'company-parameter', grouped: false },
      //     ],
      //   },
      // },
    ],
    [
      {
        id: 'factors2-promotion-text',
        title: '',
        position: { height: 6 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Probability of Promotion by Gender\n\nAnother factor leading to high attrition among female employees is low promotion rates. Faster promotion among female employees can help improve the seniority imbalance score by allowing more female employees to occupy senior positions.\n\nBelow, we break down the historic probability of transitions from Junior to Mid-level, and Mid-level to Senior Management, split by gender.',
        },
      },
      // {
      //   id: 'factors2-promotion-bar',
      //   title: 'Probability of Promotion: Men vs. Women',
      //   position: { x: 8, y: 224, width: 32, height: 12 },
      //   plot: {
      //     chartType: 'grouped-bar',
      //     defaultMetricOptions: ['promotion_probability'],
      //     secondaryDimension: 'seniority',
      //     plotParameters: [
      //       { parameterId: 'company-parameter', grouped: false },
      //     ],
      //   },
      //   isTitleManuallySet: true,
      // },
      {
        id: 'factors3-culture-text',
        title: '',
        position: { y: 20, height: 6 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Company Culture & Sentiment\n\nThe last factor that limits the ability to attract and retain women is a toxic company culture. Even with strong hiring numbers, intolerant work cultures predictably drive attrition. Our research shows toxic corporate culture is the top predictor for resignation.\n\nWe examine differences in sentiment between men and women at {{PARAM "Company"}} across key aspects like Work-Life Balance, Diversity & Inclusion, and Career Opportunities.',
        },
      },
      {
        id: 'factors3-culture-diverging',
        title: 'Difference in sentiment ratings: Men vs. Women',
        position: { y: 26, height: 6 },
        plot: {
          chartType: 'diverging-bar',
          defaultMetricOptions: ['sentiment_top_bottom'],
          secondaryDimension: 'gender',
          comparisonTransformation: { baseline: 'second' },
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'problem1-text',
        title: '',
        position: { height: 6 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Identifying Problem Areas\n\nTo identify actionable pain points, we isolate the workforce by different granularities (Job Categories vs. Geography). By comparing the female share within these granularities to industry benchmarks, we surface the areas suffering from the most significant underrepresentation.',
        },
      },
      {
        id: 'problem1-heatmap',
        title: 'Share of women relative to industry: Role vs. Geography',
        position: { x: 8, y: 6, width: 32, height: 25 },
        plot: {
          // chartType: 'heatmap',
          secondaryDimension: 'role',
          dimensionSource: 'geography',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'recommendations-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'text',
          textHtml:
            `## Recommendations & Strategic Action Plan\n\nBased on our analysis of inflows, outflows, compensation, and culture, {{PARAM "Company"}}'s strategy to address gender diversity should focus on the following:\n\n` +
            `### 1. Optimize Talent Pipelines\n` +
            `{{#if (gt (VALUE "transitions_outflow" segment="Gender" entity="Female") (VALUE "transitions_inflow" segment="Gender" entity="Female"))}}\n` +
            `As women are overrepresented in outflows relative to inflows, the immediate focus must shift to **retention** and combating structural attrition drivers. Evaluate exit interviews to identify specific systemic issues driving departures.\n` +
            `{{else}}\n` +
            `Inflows suggest the company is successfully attracting female talent. However, the business should maintain momentum by partnering with professional organizations and university STEM programs to keep the pipeline robust.\n` +
            `{{/if}}\n\n` +
            `### 2. Pay Equity & Compensation\n` +
            `Address localized salary discrepancies identified against the industry benchmark. Implement transparent salary benchmarking and pay equity audits to ensure competitive alignment, particularly in technical and senior roles.\n\n` +
            `### 3. Seniority Imbalance & Internal Mobility\n` +
            `Expand structural mentorship and sponsorship programs to guide junior and mid-level female employees into executive tracks. A documented lack of promotion velocity directly harms the Seniority Imbalance score. Standardize promotion criteria to remove subjective biases in management reviews.\n\n` +
            `### 4. Cultural Resiliency\n` +
            `Sentiment data dictates policy. Align employee benefits closely with what resonates—such as flexible work arrangements, extended parental leave, and robust DE&I support systems. Accountability at the managerial level ensures these policies translate to real-world cultural shifts.`,
        },
      },
    ],
  ]);

  return {
    id: 'report-company-gender-diversity',
    name: 'Gender Diversity',
    type: PageType.REPORT,
    owner: {
      id: 'revelio-labs',
      username: 'revelio-labs',
    },
    tabs: [
      {
        label: 'Organization gender composition vs competitors',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'company-gender-diversity-content',
          grid,
          title: 'Gender Diversity',
          description:
            'As diversity and inclusion become integral to hiring and investment strategy, understanding the gender composition of workforces compared to competitors has become an essential part of analyzing a company. Gender diversity provides valuable insights for retention, talent acquisition, and overall organizational equality.',
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
            selectionLimit: 1,
            selectionDefault: 1,
          },
          secondaryFilters: [],
          reportParameters: [
            {
              parameterId: 'company-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'competitors-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Competitors',
              selectionLimit: MAX_REPORT_COMPETITORS,
            },
          ],
        },
      },
    ],
  };
})();
